import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckIcon from '@mui/icons-material/Check';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { isValid } from 'date-fns/isValid';
import { parse } from 'date-fns/parse';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { ALL_SOCIETYS, GET_MISSIONS_BY_SEARCH } from '@bloomays-lib/adapter.api-bloomer';
import {
  AutoComplete,
  AutocompleteOption,
  Button,
  CardItem,
  DatePicker,
  DatePickerValue,
  HrefLink,
  Modal,
  SingleLineText,
  Switch,
  TitlePart,
  TypeInput,
  ValidationTextField,
} from '@bloomays-lib/ui.shared';
import { BrowserUploadFile } from '@bloomays-lib/types.shared';
import { convertDateToStringFormat } from '@bloomays-lib/utils.shared';
import { FileRejection } from 'react-dropzone';
import {
  MissionOption,
  MissionSearchAutoComplete,
  MISSION_SEARCH_MIN_LENGTH,
} from '../molecules/MissionSearchAutoComplete';
import { PurchaseOrderDatePresets } from '../molecules/PurchaseOrderDatePresets';
import { Upload } from '../molecules/Upload';
import { notify } from '../../helpers/toastify';
import { PURCHASE_ORDER_PDF_FILE_TYPES } from '../../helpers/purchaseOrderDashboard';

export type PurchaseOrderFormValues = {
  orderNumber: string;
  clientSocietyRecordId?: string;
  clientSocietyName?: string;
  missionsRecordId?: string[];
  initialMissions?: MissionOption[];
  startDate?: string;
  endDate?: string;
  amountHT?: number;
  invoicedAmountHT?: number;
  invoiceComment?: string;
  useForNonWorkingHours?: boolean;
};

export type PurchaseOrderFormSubmitContext = {
  file?: File;
};

export type PurchaseOrderFormModalProps = {
  open: boolean;
  mode: 'create' | 'edit';
  recordId?: string;
  fileUrl?: string | null;
  initialValues?: Partial<PurchaseOrderFormValues>;
  pending?: boolean;
  uploadPending?: boolean;
  onSubmit: (values: PurchaseOrderFormValues, context?: PurchaseOrderFormSubmitContext) => void | Promise<void>;
  onUploadFile?: (file: File) => void | Promise<void>;
  onClose: () => void;
  /**
   * Date de référence utilisée pour les préréglages de période (mois / trimestre / semestre).
   * Vaut l'heure courante par défaut ; à surcharger uniquement pour un rendu
   * déterministe (tests, snapshots Storybook/Chromatic).
   */
  now?: Date;
};

type SocietyQueryResult = {
  recordId: string;
  name?: string | null;
  isClient?: boolean | null;
  siret?: string | null;
};

type MissionSearchResult = {
  recordId: string;
  label?: string | null;
  missionCode?: string | null;
  bloomerFullName?: string[] | null;
  clientSocietyName?: string[] | null;
  clientSociety?: { recordId: string } | null;
  clientBillingSociety?: { recordId: string } | null;
};

const fieldSx = {
  margin: 0,
  '& .MuiFormHelperText-root': { margin: 0, minHeight: 0 },
};

const toNumberOrUndefined = (value: string): number | undefined => {
  if (value.trim() === '') {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const toStringValue = (value: number | undefined): string =>
  value === undefined || value === null ? '' : String(value);

const toDateOrNull = (value?: string): Date | null => {
  if (!value) {
    return null;
  }
  const parsed = parse(value, 'yyyy-MM-dd', new Date());
  return isValid(parsed) ? parsed : null;
};

const toDateInputValue = (value: Date | null): string | undefined =>
  value ? convertDateToStringFormat(value, 'yyyy-MM-dd') : undefined;

const toSocietyDisplayName = (society: SocietyQueryResult): string => {
  if (!society.name) {
    return society.recordId;
  }
  return society.siret ? `${society.name} (${society.siret})` : society.name;
};

const toSocietyOptions = (societies: ReadonlyArray<SocietyQueryResult>): AutocompleteOption[] =>
  societies
    .filter((society) => society.name && society.recordId)
    .map((society) => ({ id: society.recordId, name: toSocietyDisplayName(society) }));

const toMissionOption = (mission: MissionSearchResult): MissionOption => {
  const label = mission.label ?? 'Mission sans libellé';
  const missionCode = mission.missionCode?.trim();
  const bloomer = mission.bloomerFullName?.[0];
  const client = mission.clientSocietyName?.[0];
  // Le code mission est affiché en tête : c'est la référence que porte le BDC client (trigramme).
  const parts = [missionCode ? `[${missionCode}] ${label}` : label];
  if (bloomer) {
    parts.push(bloomer);
  }
  if (client) {
    parts.push(client);
  }
  return {
    id: mission.recordId,
    name: parts.join(' — '),
    clientSocietyRecordId: mission.clientSociety?.recordId,
    clientBillingSocietyRecordId: mission.clientBillingSociety?.recordId,
  };
};

const missionBelongsToClient = (mission: MissionOption, clientSocietyRecordId: string): boolean =>
  mission.clientSocietyRecordId === clientSocietyRecordId ||
  mission.clientBillingSocietyRecordId === clientSocietyRecordId;

const fileNameFromUrl = (url: string): string => {
  try {
    const segments = new URL(url).pathname.split('/');
    return segments[segments.length - 1] || url;
  } catch {
    return url;
  }
};

const toPreloadedFiles = (fileUrl?: string | null): BrowserUploadFile[] => {
  if (!fileUrl) {
    return [];
  }
  const displayName = fileNameFromUrl(fileUrl);
  return [{ name: displayName, path: displayName, url: fileUrl }];
};

const resolveInitialSociety = (
  societies: ReadonlyArray<SocietyQueryResult>,
  initialValues?: Partial<PurchaseOrderFormValues>,
): AutocompleteOption | undefined => {
  if (!initialValues) {
    return undefined;
  }
  if (initialValues.clientSocietyRecordId) {
    const byRecordId = societies.find((society) => society.recordId === initialValues.clientSocietyRecordId);
    if (byRecordId?.name) {
      return { id: byRecordId.recordId, name: toSocietyDisplayName(byRecordId) };
    }
  }
  if (initialValues.clientSocietyName) {
    const normalizedName = initialValues.clientSocietyName.trim().toLowerCase();
    const byName = societies.find((society) => society.name?.trim().toLowerCase() === normalizedName);
    if (byName?.recordId && byName.name) {
      return { id: byName.recordId, name: toSocietyDisplayName(byName) };
    }
  }
  return undefined;
};

export const PurchaseOrderFormModal = ({
  open,
  mode,
  recordId,
  fileUrl,
  initialValues,
  pending = false,
  uploadPending = false,
  onSubmit,
  onUploadFile,
  onClose,
  now,
}: PurchaseOrderFormModalProps) => {
  const { t } = useTranslation(['purchaseOrderDashboard']);
  const [orderNumber, setOrderNumber] = useState('');
  const [selectedClientSociety, setSelectedClientSociety] = useState<AutocompleteOption | undefined>(undefined);
  const [selectedMissions, setSelectedMissions] = useState<MissionOption[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [amountHT, setAmountHT] = useState('');
  const [invoicedAmountHT, setInvoicedAmountHT] = useState('');
  const [invoiceComment, setInvoiceComment] = useState('');
  const [useForNonWorkingHours, setUseForNonWorkingHours] = useState(false);
  const [orderNumberError, setOrderNumberError] = useState<string | null>(null);
  const [clientSocietyError, setClientSocietyError] = useState<string | null>(null);
  const [pendingSubmitFile, setPendingSubmitFile] = useState<File | undefined>(undefined);
  const [localPreloadedFiles, setLocalPreloadedFiles] = useState<BrowserUploadFile[]>([]);

  const societyQueryVariables = useMemo(() => {
    if (mode === 'create') {
      return { where: { isClient: true } };
    }
    if (initialValues?.clientSocietyRecordId) {
      return { where: { recordIds: [initialValues.clientSocietyRecordId] } };
    }
    return undefined;
  }, [mode, initialValues?.clientSocietyRecordId]);

  const societiesQuery = useQuery<{ allSocietys: SocietyQueryResult[] }>(ALL_SOCIETYS, {
    skip: !open || (mode === 'edit' && !initialValues?.clientSocietyRecordId),
    variables: societyQueryVariables,
  });

  const [searchMissions, { loading: missionsLoading }] = useLazyQuery<{ getMission: MissionSearchResult[] }>(
    GET_MISSIONS_BY_SEARCH,
  );

  const clientSocietyOptions = useMemo<AutocompleteOption[]>(
    () => toSocietyOptions(societiesQuery.data?.allSocietys ?? []),
    [societiesQuery.data],
  );

  const collectMissions = useCallback(
    async (input: string): Promise<MissionOption[]> => {
      const clientSocietyRecordId = selectedClientSociety?.id;
      if (input.trim().length < MISSION_SEARCH_MIN_LENGTH || !clientSocietyRecordId) {
        return [];
      }
      const response = await searchMissions({ variables: { reasearchField: input } });
      return (response.data?.getMission ?? [])
        .map(toMissionOption)
        .filter((mission) => missionBelongsToClient(mission, clientSocietyRecordId));
    },
    [searchMissions, selectedClientSociety?.id],
  );

  const handleClientSocietyChange = (society: AutocompleteOption | undefined) => {
    setSelectedClientSociety(society);
    setSelectedMissions((previousMissions) => {
      if (!society?.id) {
        return [];
      }
      return previousMissions.filter((mission) => missionBelongsToClient(mission, society.id));
    });
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    setOrderNumber(initialValues?.orderNumber ?? '');
    setStartDate(toDateOrNull(initialValues?.startDate));
    setEndDate(toDateOrNull(initialValues?.endDate));
    setAmountHT(toStringValue(initialValues?.amountHT));
    setInvoicedAmountHT(toStringValue(initialValues?.invoicedAmountHT));
    setInvoiceComment(initialValues?.invoiceComment ?? '');
    setUseForNonWorkingHours(initialValues?.useForNonWorkingHours ?? false);
    setOrderNumberError(null);
    setClientSocietyError(null);
    setPendingSubmitFile(undefined);
    setLocalPreloadedFiles(toPreloadedFiles(fileUrl));
    const resolvedClientSociety = resolveInitialSociety(societiesQuery.data?.allSocietys ?? [], initialValues);
    const clientSocietyRecordId = resolvedClientSociety?.id ?? initialValues?.clientSocietyRecordId;
    const clientSocietyDisplayName =
      resolvedClientSociety?.name ??
      initialValues?.clientSocietyName ??
      (clientSocietyRecordId ? clientSocietyRecordId : undefined);
    setSelectedClientSociety(
      clientSocietyRecordId && clientSocietyDisplayName
        ? { id: clientSocietyRecordId, name: clientSocietyDisplayName }
        : resolvedClientSociety,
    );
    setSelectedMissions(
      (initialValues?.initialMissions ?? []).map((mission) => ({
        ...mission,
        clientSocietyRecordId: mission.clientSocietyRecordId ?? clientSocietyRecordId,
      })),
    );
  }, [open, initialValues, mode, societiesQuery.data, fileUrl]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setLocalPreloadedFiles(toPreloadedFiles(fileUrl));
  }, [fileUrl, open]);

  const handleUploadChange = async (files: BrowserUploadFile[], rejected: FileRejection[]) => {
    if (rejected.length > 0) {
      return;
    }
    const selectedFile = files[0];
    if (!selectedFile) {
      return;
    }
    if (recordId && onUploadFile) {
      await onUploadFile(selectedFile as File);
      return;
    }
    setPendingSubmitFile(selectedFile as File);
    setLocalPreloadedFiles(files);
  };

  const handleSubmit = async () => {
    let hasError = false;
    if (orderNumber.trim() === '') {
      setOrderNumberError(t('formOrderNumberRequired'));
      hasError = true;
    } else {
      setOrderNumberError(null);
    }
    if (mode === 'create' && !selectedClientSociety?.id) {
      setClientSocietyError(t('formClientRequired'));
      hasError = true;
    } else {
      setClientSocietyError(null);
    }
    if (hasError) {
      return;
    }
    const linkedMissionsRecordId = selectedClientSociety?.id
      ? selectedMissions
          .filter((mission) => missionBelongsToClient(mission, selectedClientSociety.id))
          .map((mission) => mission.id)
      : [];
    await onSubmit(
      {
        orderNumber: orderNumber.trim(),
        clientSocietyRecordId: selectedClientSociety?.id ?? initialValues?.clientSocietyRecordId,
        missionsRecordId: linkedMissionsRecordId.length > 0 ? linkedMissionsRecordId : undefined,
        startDate: toDateInputValue(startDate),
        endDate: toDateInputValue(endDate),
        amountHT: toNumberOrUndefined(amountHT),
        invoicedAmountHT: mode === 'edit' ? toNumberOrUndefined(invoicedAmountHT) : undefined,
        invoiceComment: invoiceComment.trim() === '' ? undefined : invoiceComment.trim(),
        useForNonWorkingHours,
      },
      pendingSubmitFile ? { file: pendingSubmitFile } : undefined,
    );
  };

  const headerIcon =
    mode === 'create' ? (
      <CardItem variant="blue" size="small" emoji={<AddCircleOutlineIcon fontSize="small" />} />
    ) : (
      <CardItem variant="blue" size="small" emoji={<EditOutlinedIcon fontSize="small" />} />
    );

  return (
    <Modal
      open={open}
      onClose={onClose}
      additionnalCSS={{
        width: '100%',
        maxWidth: 560,
        maxHeight: '90vh',
        padding: '24px 32px 20px',
        borderRadius: '16px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      additionnalCSSCross={{
        position: 'absolute',
        top: 16,
        right: 16,
        float: 'none',
        zIndex: 1,
      }}
    >
      <FormContainer>
        <ModalHeader>
          {headerIcon}
          <TitlePart
            variant="heading6"
            textTitle={mode === 'create' ? t('formTitleCreate') : t('formTitleEdit')}
            sx={{ margin: 0, lineHeight: 1.3 }}
          />
        </ModalHeader>

        <FormBody>
          {mode === 'create' ? (
            <FullWidthField>
              <AutoComplete
                data={clientSocietyOptions}
                label={t('formClient')}
                value={selectedClientSociety}
                setValue={(value) => {
                  const nextValue = typeof value === 'function' ? value(selectedClientSociety) : value;
                  handleClientSocietyChange(nextValue);
                }}
              />
              {clientSocietyError ? <FieldError text={clientSocietyError} /> : null}
            </FullWidthField>
          ) : (
            <FullWidthField>
              <ValidationTextField
                type={TypeInput.text}
                label={t('formClient')}
                value={selectedClientSociety?.name ?? ''}
                onChange={() => undefined}
                size="small"
                error={false}
                disabled
                sx={fieldSx}
              />
            </FullWidthField>
          )}

          <FullWidthField>
            <MissionSearchAutoComplete
              label={t('formMission')}
              placeholder={selectedClientSociety?.id ? t('formMissionPlaceholder') : t('formMissionSelectClientFirst')}
              value={selectedMissions}
              disabled={!selectedClientSociety?.id}
              onChange={setSelectedMissions}
              collectMissions={collectMissions}
              loading={missionsLoading}
            />
          </FullWidthField>

          <TwoColRow>
            <FieldColumn>
              <ValidationTextField
                type={TypeInput.text}
                label={t('formOrderNumber')}
                placeholder={t('formOrderNumberPlaceholder')}
                value={orderNumber}
                onChange={(event) => setOrderNumber(event.target.value)}
                size="small"
                required
                error={!!orderNumberError}
                helperText={orderNumberError ?? undefined}
                sx={fieldSx}
              />
            </FieldColumn>
            <FieldColumn>
              <ValidationTextField
                type={TypeInput.number}
                label={t('formAmountHT')}
                placeholder={t('formAmountPlaceholder')}
                value={amountHT}
                onChange={(event) => setAmountHT(event.target.value)}
                size="small"
                error={false}
                sx={fieldSx}
              />
            </FieldColumn>
          </TwoColRow>

          {mode === 'edit' ? (
            <HalfWidthField>
              <ValidationTextField
                type={TypeInput.number}
                label={t('formInvoicedAmountHT')}
                value={invoicedAmountHT}
                onChange={(event) => setInvoicedAmountHT(event.target.value)}
                size="small"
                error={false}
                sx={fieldSx}
              />
            </HalfWidthField>
          ) : null}

          <PurchaseOrderDatePresets
            startDate={startDate}
            endDate={endDate}
            now={now}
            onSelect={(presetStartDate, presetEndDate) => {
              setStartDate(presetStartDate);
              setEndDate(presetEndDate);
            }}
          />

          <TwoColRow>
            <FieldColumn>
              <DatePicker
                ariaLabel="purchase-order-start-date"
                label={t('formStartDate')}
                value={startDate}
                maxDate={endDate ?? undefined}
                handleChange={(date: DatePickerValue) => setStartDate(date)}
                sx={{ width: '100%', ...fieldSx }}
              />
            </FieldColumn>
            <FieldColumn>
              <DatePicker
                ariaLabel="purchase-order-end-date"
                label={t('formEndDate')}
                value={endDate}
                minDate={startDate ?? undefined}
                handleChange={(date: DatePickerValue) => setEndDate(date)}
                sx={{ width: '100%', ...fieldSx }}
              />
            </FieldColumn>
          </TwoColRow>

          <OptionalSection>
            <FullWidthField>
              <ValidationTextField
                type={TypeInput.text}
                label={t('formInvoiceComment')}
                value={invoiceComment}
                onChange={(event) => setInvoiceComment(event.target.value)}
                size="small"
                error={false}
                sx={fieldSx}
              />
            </FullWidthField>

            <Switch
              id="purchase-order-use-for-non-working-hours"
              label={t('formUseForNonWorkingHours')}
              checked={useForNonWorkingHours}
              onSwitch={(_event, checked) => setUseForNonWorkingHours(checked)}
            />
          </OptionalSection>

          <FileSection>
            <SingleLineText text={t('formFile')} variant="body2" />
            {fileUrl ? (
              <ExistingFileBox>
                <FileHint variant="caption">{t('formFileCurrent')}</FileHint>
                <HrefLink href={fileUrl} target="_blank">
                  {fileNameFromUrl(fileUrl)}
                </HrefLink>
              </ExistingFileBox>
            ) : mode === 'edit' ? (
              <FileHint variant="caption">{t('formFileNone')}</FileHint>
            ) : (
              <FileHint variant="caption">{t('formFileHintCreate')}</FileHint>
            )}
            {fileUrl ? <FileHint variant="caption">{t('formFileReplaceHint')}</FileHint> : null}
            <Upload
              id="purchase-order-form-file-upload"
              textLabel={t('formFileUploadLabel')}
              placeholder={t('formFilePlaceholder')}
              maxLength={5000000}
              maxFiles={1}
              fileTypes={PURCHASE_ORDER_PDF_FILE_TYPES}
              preloadedFiles={localPreloadedFiles}
              handleChange={handleUploadChange}
              autoload
              displayIcons
              isLoading={uploadPending}
              noDrop
            />
          </FileSection>
        </FormBody>

        <ModalFooter>
          <Button color="secondary" textButton={t('formCancel')} onClick={onClose} />
          <Button
            ariaLabel="purchase-order-form-submit"
            textButton={mode === 'create' ? t('formSubmitCreate') : t('formSubmit')}
            emoji={mode === 'create' ? <CheckIcon fontSize="small" /> : undefined}
            onClick={handleSubmit}
            disable={pending || uploadPending}
          />
        </ModalFooter>
      </FormContainer>
    </Modal>
  );
};

export default PurchaseOrderFormModal;

const FormContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  flex: 1,
  minHeight: 0,
  maxHeight: '100%',
}));

const ModalHeader = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  paddingRight: 36,
  marginBottom: 8,
  flexShrink: 0,
}));

const FormBody = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  paddingRight: 4,
  paddingTop: 8,
}));

const TwoColRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: 16,
  alignItems: 'flex-start',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const FieldColumn = styled('div')(() => ({
  flex: 1,
  minWidth: 0,
  width: '100%',
}));

const FullWidthField = styled('div')(() => ({
  width: '100%',
}));

const HalfWidthField = styled('div')(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    width: 'calc(50% - 8px)',
  },
}));

const OptionalSection = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  paddingTop: 4,
}));

const FileSection = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: 12,
  borderRadius: 8,
  border: `1px solid ${theme.palette.neutral?.[15] ?? '#e5e7eb'}`,
}));

const ExistingFileBox = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
}));

const FileHint = styled(SingleLineText)(({ theme }) => ({
  color: theme.palette.neutral?.[60] ?? '#6b7280',
}));

const FieldError = styled(SingleLineText)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: '0.75rem',
  marginLeft: '14px',
  marginTop: 4,
}));

const ModalFooter = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: 12,
  paddingTop: 16,
  marginTop: 4,
  borderTop: `1px solid ${theme.palette.neutral[15]}`,
  flexShrink: 0,
  backgroundColor: theme.palette.background.paper,
}));
